import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../reducers';
import {
    Box,
    Button,
    CircularProgress,
    Collapse,
    createTheme,
    FormControl,
    FormControlLabel,
    Grid,
    Paper,
    Radio,
    TextField,
    Typography,
    Stack,
    Tooltip,
} from '@mui/material';
import { gotoNextMenu, gotoPreviousMenu } from '../../../store/assessment.slice';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AlertDialog from '../../AlertDialog/AlertDialog';
import {
    fetchGAFScaleInfo,
    fetchGAFScaleInfoByMemberId,
    fetchGAFScaleOptions,
    saveGAFScaleAssessment,
    setGAFScaleInfo,
} from '../../../store/gafScaleSlice.slice';
import { GAFScaleModel } from '../../../Models/GAFScale/GAFScale.model';
import { copyObject } from '../../../utils/common';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import { AssessmentQuestions } from '../../../utils/assessmentQuestions';
import { isAIIcon } from '../Assessment';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Cultural_Considerations_Eating_Disorder_GAF_Scale.pdf';

const theme = createTheme();
const classes = {
    form: {
        '& .MuiCheckbox-root': {
            marginLeft: '-10px',
        },
    },
    root: {
        '& .MuiGrid-root': {
            marginBottom: theme.spacing(2),
        },
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
            width: '90%',
        },
    },
};

const GAFScale: React.FC<{ memberId: string }> = ({ memberId }) => {
    const params = useParams();
    memberId = params.memberId;
    const dispatch = useAppDispatch();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const { gafScaleInfo, gafScaleOptions, isFetchingGAFScale } = useSelector((state: RootState) => state.gafScaleSlice);
    const { engagementDraft, memberAssessmentHistory, isSaving } = useSelector((state: RootState) => state.documentEngagement);
    const saving = isSaving || isFetchingGAFScale;
    const [isChanged, setIsChanged] = useState<boolean>(false);

    const gafScaleInfoIsComplete = (values: GAFScaleModel) => {
        const isComplete = Boolean(values.GAFScore) && Boolean(values.GAFScaleComments);
        return isComplete;
    };

    const handleSubmit = () => {
        setShowSaveAlert(true);
    };

    const handleOnConfirmToNext = (gafScaleInfo: GAFScaleModel, isComplete = false) => {
        const copy = copyObject(gafScaleInfo);
        copy.MemberId = memberId;
        copy.IsAssessmentCompleted = isComplete;
        if (Boolean(isComplete)) {
            copy.AssessmentStatus = StatusEnum.Completed;
            dispatch(saveGAFScaleAssessment(copy, false, true));
        } else {
            copy.AssessmentStatus = StatusEnum.InProgress;
            dispatch(saveGAFScaleAssessment(copy, true, true));
        }
    };

    const handleOnConfirmSkip = () => {
        dispatch(setGAFScaleInfo(new GAFScaleModel()));
        dispatch(gotoNextMenu());
    };

    useEffect(() => {
        if (engagementDraft?.GAFScaleId) {
            dispatch(fetchGAFScaleInfo(engagementDraft.GAFScaleId));
        } else {
            dispatch(fetchGAFScaleInfoByMemberId(memberId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(fetchGAFScaleOptions());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
            <Grid container sx={classes.form} direction="column" spacing={2} className="content-panel">
                <Grid>
                    {saving ? (
                        <Box display="flex" justifyContent="center" className="loader-container">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2} sx={classes.root}>
                                <Grid size={12}>
                                    <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                        <Typography variant="h6" component="label">
                                            {AssessmentQuestions.GAFScale_GAFScale}
                                        </Typography>
                                        <Tooltip title="GAF Scale Help Document">
                                            <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                                        </Tooltip>
                                    </Stack>

                                    <hr />
                                </Grid>
                                <Grid size={9}>
                                    <Typography variant="body1" fontStyle={'italic'} component="label">
                                        {AssessmentQuestions.GAFScale_GAFScore}
                                        {gafScaleInfo.IsAIGAFScore && isAIIcon()}
                                    </Typography>
                                </Grid>
                                <Grid size={9}>
                                    <FormControl sx={{ display: 'flex', pl: 2 }} component="fieldset" variant="standard" fullWidth>
                                        <Grid container spacing={2}>
                                            {gafScaleOptions.map((option, index) => {
                                                return (
                                                    <React.Fragment key={index}>
                                                        <Grid size={12} key={option.Id}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Radio
                                                                        checked={gafScaleInfo.GAFScore === option.Value}
                                                                        onChange={(e) => {
                                                                            dispatch(
                                                                                setGAFScaleInfo({
                                                                                    ...gafScaleInfo,
                                                                                    GAFScore: e.target.value,
                                                                                    IsAIGAFScore: false,
                                                                                })
                                                                            );
                                                                            setIsChanged(true);
                                                                        }}
                                                                        value={option.Value}
                                                                        name="GAFScore"
                                                                    />
                                                                }
                                                                label={
                                                                    <Stack spacing={0}>
                                                                        <Typography variant="h6" component="label" fontWeight={'bold'}>
                                                                            {option.Value}
                                                                        </Typography>
                                                                        <Typography variant="body1" component="label">
                                                                            {option.Description}
                                                                        </Typography>
                                                                    </Stack>
                                                                }
                                                            />
                                                        </Grid>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </Grid>
                                    </FormControl>
                                </Grid>

                                <Grid size={9} sx={{ mt: -2 }}>
                                    <Collapse in={gafScaleInfo.GAFScore !== ''} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                                        <TextField
                                            label={AssessmentQuestions.GAFScale_Comments}
                                            variant="outlined"
                                            size="small"
                                            multiline
                                            required
                                            rows={3}
                                            fullWidth
                                            margin="normal"
                                            name="GAFScaleComments"
                                            value={gafScaleInfo.GAFScaleComments || ''}
                                            onChange={(e) => {
                                                dispatch(setGAFScaleInfo({ ...gafScaleInfo, GAFScaleComments: e.target.value }));
                                                setIsChanged(true);
                                            }}
                                        />
                                    </Collapse>
                                </Grid>
                            </Grid>

                            <Grid justifyContent="flex-end" className="content-footer">
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    sx={{ marginRight: 2 }}
                                    disabled={saving}
                                    onClick={() => dispatch(gotoPreviousMenu())}
                                    startIcon={<ArrowBackIosIcon />}
                                >
                                    Previous
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={saving}
                                    endIcon={saving ? <CircularProgress size={18} /> : <ArrowForwardIosIcon />}
                                    onClick={handleSubmit}
                                >
                                    Next
                                </Button>
                            </Grid>
                        </>
                    )}

                    {Boolean(gafScaleInfoIsComplete(gafScaleInfo) && gafScaleInfo.CreatedBy && !isChanged) ? (
                        <AlertDialog
                            open={showSaveAlert}
                            title={`Global Assessment of Functioning (GAF) Scale`}
                            buttonsDirection="column-reverse"
                            showBottomCenter={true}
                            cancelText="Go Back"
                            skipText={`GAF Scale Assessment Not Reviewed`}
                            okText={`Agree With GAF Scale Assessment`}
                            onConfirm={() => handleOnConfirmToNext(gafScaleInfo, true)}
                            onSkip={handleOnConfirmSkip}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={isSaving}
                            message={`Last GAF Scale assessment completed on ${(() => {
                                const d = toDate(gafScaleInfo.UpdatedDate ?? gafScaleInfo.CreatedDate);
                                return d ? formatDate(d, 'MM/dd/yyyy') : '';
                            })()}.`}
                        />
                    ) : Boolean(gafScaleInfoIsComplete(gafScaleInfo)) ? (
                        <AlertDialog
                            open={showSaveAlert}
                            title="Global Assessment of Functioning (GAF) Scale"
                            cancelText="Go Back"
                            okText="Confirm"
                            onConfirm={() => handleOnConfirmToNext(gafScaleInfo, true)}
                            onSkip={handleOnConfirmSkip}
                            showBottomCenter={true}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={saving}
                            message="I confirm and acknowledge that GAF Scale is completed."
                        />
                    ) : (
                        <AlertDialog
                            open={showSaveAlert}
                            title="Global Assessment of Functioning (GAF) Scale"
                            showBottomCenter={true}
                            cancelText="Go Back"
                            skipText="Skip and continue"
                            okText="Confirm"
                            onConfirm={() => handleOnConfirmToNext(gafScaleInfo, false)}
                            onSkip={handleOnConfirmSkip}
                            onClose={() => setShowSaveAlert(false)}
                            isLoading={saving}
                            message="Are you sure you want to proceed without completing the GAF Scale assessment?"
                        />
                    )}
                </Grid>
            </Grid>
        </Paper>
    );
};
export default GAFScale;
